// Helpers for playing call recordings.
//
// Twilio recording URLs require HTTP Basic Auth — the browser <audio> element
// can't supply that header, so it shows a sign-in popup if you point it at
// api.twilio.com directly. Instead we route through the CIVIX recordings proxy
// (`/api/recordings/:callId/stream`) which authenticates with Twilio server-
// side and pipes the bytes back.
//
// Hotfix (May 2026): the proxy USED to accept the user's long-lived JWT in
// `?token=...`. That JWT leaks via browser history, server access logs, and
// referer headers — a single leaked log entry = full account takeover. We
// now fetch a short-lived (≤5 min), recording-scoped token from
// /api/recordings/:callId/token and use that in the URL instead. Callers
// that previously called `recordingStreamUrl(id)` synchronously must now
// await the result.

const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');

// Per-callId promise cache so a component that mounts <audio src> twice
// doesn't hit the mint endpoint twice. The token is short-lived but valid
// across multiple stream requests during its TTL.
const tokenCache = new Map();

async function fetchRecordingToken(callId) {
  const jwt = (typeof localStorage !== 'undefined' && localStorage.getItem('civix_token')) || '';
  const res = await fetch(`${apiBase}/recordings/${callId}/token`, {
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`recording token mint failed: ${res.status}`);
  }
  const body = await res.json();
  return { token: body.token, expiresAt: Date.now() + ((body.expiresIn || 300) * 1000) - 5000 };
}

function getCachedToken(callId) {
  const cached = tokenCache.get(callId);
  if (!cached) return null;
  return cached.then((v) => {
    if (!v || !v.token || Date.now() >= v.expiresAt) return null;
    return v.token;
  }).catch(() => null);
}

/**
 * Build the streaming URL for a recording, tied to the given CIVIX call id
 * (either a PhoneCall._id or a Call._id).
 *
 * Returns a Promise<string> — callers must await. On any error the function
 * returns an empty string so <audio> elements simply fail gracefully (the
 * previous JWT-in-URL path could not authenticate anyway in that case).
 */
export async function recordingStreamUrl(callId) {
  if (!callId) return '';
  let entry = tokenCache.get(callId);
  if (entry) {
    const existing = await entry.catch(() => null);
    if (!existing || Date.now() >= existing.expiresAt) {
      entry = null;
      tokenCache.delete(callId);
    }
  }
  if (!entry) {
    entry = fetchRecordingToken(callId).catch((err) => {
      tokenCache.delete(callId);
      throw err;
    });
    tokenCache.set(callId, entry);
  }
  try {
    const { token } = await entry;
    if (!token) return '';
    return `${apiBase}/recordings/${callId}/stream?token=${encodeURIComponent(token)}`;
  } catch {
    return '';
  }
}

// Exposed for tests / debugging.
export function _clearRecordingTokenCache() {
  tokenCache.clear();
}

// React-side helpers ─────────────────────────────────────────────────────────
import { useEffect, useState } from 'react';

/**
 * useRecordingStreamUrl(callId) — resolves the per-callId stream URL when
 * the component mounts (or callId changes). Returns the URL string once
 * minted, or empty string while loading / on failure.
 *
 * @deprecated Use useRecordingStream(callId) instead — that variant returns
 * { url, status } so callers can distinguish 'loading' from 'error'.
 */
export function useRecordingStreamUrl(callId) {
  return useRecordingStream(callId).url;
}

/**
 * useRecordingStream(callId) — richer variant of useRecordingStreamUrl.
 * Returns `{ url, status }` where status ∈ { 'loading', 'ready', 'error' }.
 *
 * Audit fix 2026-08-25: the previous single-string return made an error
 * state indistinguishable from a still-loading state. Recordings whose
 * token mint failed showed "Loading recording…" forever instead of
 * surfacing the failure.
 */
export function useRecordingStream(callId) {
  const [state, setState] = useState({ url: '', status: callId ? 'loading' : 'ready' });
  useEffect(() => {
    if (!callId) {
      setState({ url: '', status: 'ready' });
      return undefined;
    }
    let cancelled = false;
    setState({ url: '', status: 'loading' });
    recordingStreamUrl(callId)
      .then((u) => {
        if (cancelled) return;
        setState({ url: u, status: u ? 'ready' : 'error' });
      })
      .catch(() => {
        if (!cancelled) setState({ url: '', status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);
  return state;
}

/**
 * Resolve the URL imperatively (for click handlers / `new Audio()` flows).
 * Returns a Promise<string>. Identical to `recordingStreamUrl` — exported
 * under a verb-y name so call sites read better.
 */
export async function openRecordingStream(callId) {
  return recordingStreamUrl(callId);
}
